class CustomSelectV3 extends HTMLElement {
  constructor() {
    super();
    this.options = [];
    this.selectedValue = null;
    this._placeholder = "Select an option";
    this._pendingOptions = null;
    this._pendingValue = null;

    this._onHeaderClick = this._onHeaderClick.bind(this);
    this._onOptionsClick = this._onOptionsClick.bind(this);
    this._onDocumentClick = this._onDocumentClick.bind(this);
  }

  static get observedAttributes() {
    return ["options", "class", "placeholder", "value"];
  }

  connectedCallback() {
    this.render();

    // DOM 渲染完再做初始化
    requestAnimationFrame(() => {
      this._addEventListeners();
      this._applyInitialAttributes(); // 初始化属性
    });
  }

  disconnectedCallback() {
    this._removeEventListeners();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;

    if (name === "placeholder") {
      this._placeholder = newValue || "Select an option";
      this._updatePlaceholder();
    } else if (name === "options") {
      try {
        const parsed = JSON.parse(newValue);
        this._pendingOptions = parsed; // 缓存下来，延迟处理
        if (this.isConnected && this._isDOMReady()) {
          this.updateOptions(parsed);
        }
      } catch (e) {
        console.error("Invalid options format:", e);
      }
    } else if (name === "class") {
      this._updateClass(newValue);
    } else if (name === "value") {
      this._pendingValue = newValue;
      if (this.isConnected && this._isDOMReady()) {
        this.selectOption(newValue);
      }
    }
  }

  get value() {
    return this.selectedValue;
  }

  set value(val) {
    this.selectOption(val);
  }

  render() {
    this.innerHTML = `
      <div class="custom-select-v3">
        <div class="custom-select-v3-header">
          <div class="custom-select-v3-arrow">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2.88477 5.58423L7.71656 10.416L8.35352 11.053L8.99047 10.416L13.8223 5.58423L13.1853 4.94727L8.35352 9.77908L3.5217 4.94727L2.88477 5.58423Z" fill="black"/>
            </svg>
          </div>
          <span>${this._placeholder}</span>
        </div>
        <div class="custom-select-v3-options segway-scroll-bar"></div>
      </div>
    `;
  }

  updateOptions(options) {
    if (!Array.isArray(options)) {
      console.error("Options must be an array.");
      return;
    }

    this.options = options;

    const container = this.querySelector(".custom-select-v3-options");
    if (!container) return;

    container.innerHTML = this.options
      .map(option => `
        <div class="custom-select-v3-option${option.value === this.selectedValue ? " selected" : ""}" 
             data-value="${option.value}"
             ${option.code ? `data-code="${option.code}"` : ""}
             ${option.next ? `data-next="${option.next}"` : ""}>
          ${option.label}
        </div>
      `)
      .join("");
  }

  selectOption(value) {
    const selected = this.options.find(opt => opt.value === value);
    if (!selected) return;

    this.selectedValue = selected.value;

    const span = this.querySelector(".custom-select-v3-header span");
    if (span) {
      span.textContent = selected.label;
    }

    this.querySelectorAll(".custom-select-v3-option").forEach(el => {
      el.classList.toggle("selected", el.getAttribute("data-value") === value);
    });

    this.querySelector(".custom-select-v3-options")?.classList.remove("active");
    this.querySelector(".custom-select-v3-header")?.classList.remove("active");

    this.dispatchEvent(new CustomEvent("custom-select-v3", {
      detail: {
        value: selected.value,
        label: selected.label,
        next: selected.next || false,
        code: selected.code || ""
      },
      bubbles: true,
      composed: true
    }));
  }

  reset() {
    this.selectedValue = null;
    this._updatePlaceholder();
    this.querySelector(".custom-select-v3-options")?.classList.remove("active");
  }

  _addEventListeners() {
    this.querySelector(".custom-select-v3-header")?.addEventListener("click", this._onHeaderClick);
    this.querySelector(".custom-select-v3-options")?.addEventListener("click", this._onOptionsClick);
    document.addEventListener("click", this._onDocumentClick);
  }

  _removeEventListeners() {
    this.querySelector(".custom-select-v3-header")?.removeEventListener("click", this._onHeaderClick);
    this.querySelector(".custom-select-v3-options")?.removeEventListener("click", this._onOptionsClick);
    document.removeEventListener("click", this._onDocumentClick);
  }

  _onHeaderClick() {
    this.querySelector(".custom-select-v3-options")?.classList.toggle("active");
    this.querySelector(".custom-select-v3-header")?.classList.toggle("active");
  }

  _onOptionsClick(event) {
    const option = event.target.closest(".custom-select-v3-option");
    if (option) {
      window.dataLayer.push({ "event_parameters": null });
      window.dataLayer.push({
        event:"ga4Event",
        event_name: "map_series_click",
        event_parameters: {
          current_page: window.location.href,
          heading: "",
          button_name: option.textContent.trim(),
        }
      });
      this.selectOption(option.getAttribute("data-value"));
    }
  }

  _onDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.querySelector(".custom-select-v3-options")?.classList.remove("active");
      this.querySelector(".custom-select-v3-header")?.classList.remove("active");
    }
  }

  _updateClass(newClass) {
    this.querySelector(".custom-select-v3")?.classList.add(newClass);
    this.querySelector(".custom-select-v3-header")?.classList.add(newClass);
  }

  _updatePlaceholder() {
    if (!this.selectedValue) {
      const span = this.querySelector(".custom-select-v3-header span");
      if (span) {
        span.textContent = this._placeholder;
      }
    }
  }

  _applyInitialAttributes() {
    if (this._pendingOptions) {
      this.updateOptions(this._pendingOptions);
      this._pendingOptions = null;
    }

    if (this._pendingValue) {
      this.selectOption(this._pendingValue);
      this._pendingValue = null;
    }

    this._updatePlaceholder();
  }

  _isDOMReady() {
    return !!this.querySelector(".custom-select-v3-options");
  }
}

customElements.define("custom-select-v3", CustomSelectV3);
